import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SidebarNavigation from '../components/SidebarNavigation';
import { isAuthenticated } from '../lib/auth';

const PrivacyPolicy = () => {
  const isAuth = isAuthenticated();

  return (
    <div className="min-h-screen bg-gray-50">
      {isAuth && <SidebarNavigation />}
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-600 mb-8">Last updated: November 2025</p>

          <div className="space-y-8 text-slate-700">
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Information We Collect</h2>
              <p className="leading-relaxed">
                We collect information you provide directly to us when you create an account, use our services, 
                or communicate with us. This includes your name, email address, profile information, and any 
                content you create using our platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 leading-relaxed">
                <li>To provide, maintain, and improve our services</li>
                <li>To send you technical notices and updates</li>
                <li>To respond to your comments and questions</li>
                <li>To send you promotional communications (if you've opted in)</li>
                <li>To protect against fraudulent or illegal activity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. Google Calendar Integration</h2>
              <p className="leading-relaxed mb-3">
                When you connect your Google Calendar to DigiDreamSolutions:
              </p>
              <ul className="list-disc list-inside space-y-2 leading-relaxed mb-4">
                <li>We request <strong>read-only access</strong> to your calendar events</li>
                <li>We use this access solely to check your availability and prevent double bookings</li>
                <li>We do NOT modify, delete, or create events in your Google Calendar</li>
                <li>We do NOT share your calendar data with third parties</li>
                <li>You can disconnect Google Calendar at any time from your DigiCalendar settings</li>
                <li>OAuth tokens are stored securely and encrypted</li>
              </ul>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-sm text-slate-700">
                  <strong>Important:</strong> DigiDreamSolutions' use of information received from Google APIs 
                  adheres to{' '}
                  <a 
                    href="https://developers.google.com/terms/api-services-user-data-policy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    Google API Services User Data Policy
                  </a>, including the Limited Use requirements.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Information Sharing</h2>
              <p className="leading-relaxed">
                We do not sell your personal information. We may share your information with third-party service 
                providers who perform services on our behalf, such as hosting, analytics, and customer support.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. Your Rights</h2>
              <p className="leading-relaxed mb-2">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 leading-relaxed">
                <li>Access and update your personal information</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of promotional communications</li>
                <li>Request a copy of your data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Cookies</h2>
              <p className="leading-relaxed">
                We use cookies and similar tracking technologies to collect information about your browsing 
                activities and to provide a personalized experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
                <br />
                <a href="mailto:privacy@digidreamsolutions.com" className="text-blue-600 hover:underline">
                  privacy@digidreamsolutions.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;